import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Star, Share2, Check, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCompare } from '../hooks/useCompare';

// Generate SEO-friendly slug from vehicle data (brand-model-year)
const generateVehicleSlug = (v) => {
  if (!v) return '';
  const brand = (v.brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const model = (v.model || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(new RegExp(`^${brand}`, 'i'), '');
  const year = v.year || '';
  return `${brand}-${model}-${year}`;
};

const VehicleCard = React.memo(({ vehicle, lang }) => {
  const { t } = useTranslation(lang);
  const { has, toggle, max } = useCompare();
  const inCompare = has(vehicle.id);
  const images = (vehicle.images && vehicle.images.length > 0)
    ? vehicle.images
    : ['https://via.placeholder.com/400x300?text=No+Image'];
  const vehicleSlug = generateVehicleSlug(vehicle);

  // Hover mini-slideshow: cycle through images after 3s hover
  const [imgIdx, setImgIdx] = useState(0);
  const timerRef = useRef(null);
  const hoveringRef = useRef(false);

  const handleMouseEnter = () => {
    hoveringRef.current = true;
    if (images.length <= 1) return;
    // Wait 3s before starting rotation
    timerRef.current = setTimeout(function rotate() {
      if (!hoveringRef.current) return;
      setImgIdx(prev => (prev + 1) % images.length);
      timerRef.current = setTimeout(rotate, 1600);
    }, 3000);
  };

  const handleMouseLeave = () => {
    hoveringRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    setImgIdx(0);
  };

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const currentImage = images[imgIdx];

  const handleWhatsAppShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/${vehicleSlug}`;
    const text = `${vehicle.year} ${vehicle.brand} ${vehicle.model} - $${vehicle.price.toLocaleString()}\n${vehicle.mileage.toLocaleString()} miles\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const result = toggle(vehicle);
    if (result.removed) {
      toast.success(t('compare.removedFromComparison'));
    } else if (result.ok) {
      toast.success(t('vehicleDetail.addedToComparison'));
    } else if (result.reason === 'full') {
      toast.error(t('vehicleDetail.maxThreeVehicles').replace('3', String(max)));
    } else if (result.reason === 'exists') {
      toast.info(t('vehicleDetail.alreadyInComparison'));
    }
  };

  return (
    <div
      className="card group"
      data-testid={`vehicle-card-${vehicle.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative overflow-hidden">
        <img
          src={currentImage}
          alt={`${vehicle.brand} ${vehicle.model}`}
          className="w-full h-64 object-cover"
          loading="lazy"
          data-testid="vehicle-card-image"
        />
        {images.length > 1 && imgIdx > 0 && (
          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10"
            data-testid="vehicle-card-dots"
          >
            {images.slice(0, Math.min(5, images.length)).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === (imgIdx % Math.min(5, images.length))
                    ? 'w-5 bg-white'
                    : 'w-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
        {vehicle.is_featured && (
          <Badge className="absolute top-4 left-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 border-0" data-testid="featured-badge">
            <Star className="w-3 h-3 mr-1" />
            {t('vehicle.featured')}
          </Badge>
        )}
        <button
          onClick={handleCompare}
          className={`absolute top-4 right-14 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
            inCompare
              ? 'bg-amber-400 text-gray-900 opacity-100'
              : 'bg-white/95 text-gray-900 opacity-0 group-hover:opacity-100 hover:bg-white'
          }`}
          data-testid={`compare-toggle-${vehicle.id}`}
          title={inCompare ? t('compare.removedFromComparison') : t('nav.compare')}
          aria-pressed={inCompare}
        >
          {inCompare ? <Check className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
        </button>
        <button
          onClick={handleWhatsAppShare}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
          data-testid="whatsapp-share-btn"
          title="Share via WhatsApp"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-6">
        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="vehicle-card-title">
          {vehicle.brand} {vehicle.model}
        </h3>

        <div className="flex items-center justify-between mb-4">
          <span className="text-3xl money-green" data-testid="vehicle-card-price">
            ${vehicle.price?.toLocaleString()}
          </span>
          <span className="text-gray-600" data-testid="vehicle-card-year">{vehicle.year}</span>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 mb-6">
          <span data-testid="vehicle-card-mileage">{vehicle.mileage?.toLocaleString()} {t('vehicle.mileage')}</span>
          <span data-testid="vehicle-card-transmission">{vehicle.transmission}</span>
        </div>

        <Link to={`/${vehicleSlug}`}>
          <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="view-details-btn">
            <Eye className="w-4 h-4 mr-2" />
            {t('vehicle.viewDetails')}
          </Button>
        </Link>
      </div>
    </div>
  );
});

VehicleCard.displayName = 'VehicleCard';

export default VehicleCard;
